import { Flex, Button, Text, Spinner } from '@radix-ui/themes';
import { Link, useLocation } from 'react-router-dom';
import {
    HomeIcon,
    BookOpenIcon,
    UsersIcon,
    ClipboardIcon,
    CalendarIcon,
    ChartBarIcon,
    CurrencyDollarIcon,
    ArrowUpIcon
} from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import { SUBSCRIPTION_BASE_AMOUNT } from '../../utils/constants';
import { DateTime } from 'luxon';
import { updateAccountDetails, updatePaymentDetails } from '../../redux/slices/accountSlice';
import api from '../../api';
import { toast } from 'react-toastify';

const sidenavItems = {
    teacher: [
        { path: '/teacher', icon: HomeIcon, label: 'Overview' },
        { path: '/teacher/schedule', icon: CalendarIcon, label: 'Schedule' },
        { path: '/teacher/courses', icon: BookOpenIcon, label: 'Classes' },
        // { path: '/teacher/teachingPlan', icon: ClipboardIcon, label: 'Plans' },
        // { path: '/teacher/students', icon: UsersIcon, label: 'Students' },
        { path: '/teacher/reports', icon: ChartBarIcon, label: 'Reports' },
        { path: '/teacher/withdrawals', icon: CurrencyDollarIcon, label: 'Withdrawals' },
        // { path: '/teacher/settings', icon: ArrowUpIcon, label: 'Settings' }
    ],
    guardian: [
        { path: '/guardian', icon: HomeIcon, label: 'Overview' },
        { path: '/guardian/students', icon: UsersIcon, label: 'Wards' },
        { path: '/guardian/reports', icon: ChartBarIcon, label: 'Reports' },
        // { path: '/guardian/settings', icon: ArrowUpIcon, label: 'Settings' },
    ],
    school: [
        { path: '/school', icon: HomeIcon, label: 'Overview' },
        { path: '/school/teachers', icon: UsersIcon, label: 'Teachers' },
        // { path: '/school/classes', icon: BookOpenIcon, label: 'Classes' },
        { path: '/school/verifications', icon: UsersIcon, label: 'Verifications' },
        { path: '/school/withdrawals', icon: CurrencyDollarIcon, label: 'Withdrawals' },
        // { path: '/school/manage-teachers', icon: UsersIcon, label: 'Teachers' },
    ],
    student: [
        { path: '/student', icon: HomeIcon, label: 'Overview' },
        { path: '/student/courses', icon: BookOpenIcon, label: 'Courses' },
        { path: '/student/progress', icon: ChartBarIcon, label: 'Progress' },
    ]
};

export default function ConsoleSidebar({ role }) {
    const location = useLocation();
    const dispatch = useDispatch();
    // const account = useSelector(state => state.account);
    const { account, loading } = useSelector(state => ({
        account: state.account,
        loading: state.account.loading
    }));
    const user = useSelector((state) => state.user);

    const isPathActive = (path) => {
        // Skip exact match for root paths like /teacher, /student, etc
        if (path.split('/').length === 2) {
            return location.pathname === path;
        }
        // For other paths, check if the current path starts with the nav item path
        return location.pathname.startsWith(path);
    };

    const handleTrial = async () => {
        // Calculate date 7 days from now using Luxon
        const endDate = DateTime.now().plus({ days: 14 });
        // Format as YYYY-MM-DD
        const formattedEndDate = endDate.toISODate();

        try {
            const response = await api.post('/trial-payment', {
                student_id: account?.details?.id,
                received_from_id: account.details.id,
                received_from_type: 'App\\Models\\Student',
                discount_code: '2WEEKSTRIAL',
                discount_amount: SUBSCRIPTION_BASE_AMOUNT,
                amount: SUBSCRIPTION_BASE_AMOUNT,
                end_date: formattedEndDate,
                type: 'free_trial'
            });

            dispatch(updatePaymentDetails(response.data.payment_details));

            dispatch(updateAccountDetails(response.data.student));

            toast.success("Trial activated successfully, enjoy your 2 weeks trial", { position: "bottom-right" });

        } catch (error) {
            console.error('Error activating trial:', error);

            toast.error("An unexpected error occured , try again", { position: "bottom-right" });
        }
    };

    const isNearExpiry = () => {
        if (!account?.paymentDetails?.details?.end_date || account?.paymentDetails?.status !== 'active') {
            return false;
        }
        const endDate = DateTime.fromISO(account.paymentDetails.details.end_date);
        const now = DateTime.now();
        const daysUntilExpiry = endDate.diff(now, 'days').days;
        return daysUntilExpiry <= 5 && daysUntilExpiry >= 0;
    };

    return (
        <>
            {/* Desktop Sidebar */}
            <Flex
                direction="column"
                justify="start"
                align="start"
                p="4"
                gap="2"
                maxWidth="200px"
                width="100%"
                shrink="0"
                grow="0"
                className="hidden md:flex" // Hide on mobile, show on medium screens and up
                style={{
                    height: 'fit-content',
                }}
            >
                <img src="/icon-192x192.png" alt="logo" width="100%" className="sidebar-logo" />
                <Flex direction="column" justify="start">
                    {sidenavItems[role].map(({ path, icon: Icon, label }) => (
                        <Link
                            key={path}
                            to={path}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                padding: '2px',
                                marginBottom: '8px',
                                gap: '8px',
                                color: isPathActive(path) ? 'var(--accent-11)' : 'var(--gray-9)'
                            }}
                            className={isPathActive(path) ? 'active' : ''}
                        >
                            <span className="sidebar-icon">
                                <Icon className="size-4" />
                            </span>
                            <Text size="2">{label}</Text>
                        </Link>
                    ))}

                    {account?.paymentDetails?.status === 'active' && !account?.paymentDetails?.disable_payment && isNearExpiry() && (
                        <div className="mt-4">
                            <Link to="/student/payments">
                                <Button className="bg-blue-600 text-[12px] text-white rounded-lg py-4 h-[40px] w-[140px] cursor-pointer">
                                    Renew Subscription
                                </Button>
                            </Link>
                        </div>
                    )}

                    {role === 'student' && account?.paymentDetails?.status !== 'active' && (
                        <>
                            {loading ? (
                                <div className='flex justify-center items-center mt-4'>
                                    <Spinner size={16} />
                                </div>

                            ) : (
                                <div className='flex flex-col gap-2'>
                                    <div className='mt-4'>
                                        <Link to="/student/payments">
                                            <Button className="bg-green-600 text-[12px] text-white rounded-lg py-4 h-[40px] w-[140px] cursor-pointer">
                                                Upgrade to Advantage Plan
                                            </Button>
                                        </Link>
                                    </div>

                                    {!account?.details?.trial_started_at && (
                                        <Button onClick={handleTrial} className="w-full bg-red-600 text-white rounded-lg py-3 cursor-pointer p-2">
                                            Claim 2-Week Trial
                                        </Button>
                                    )}

                                </div>
                            )}
                        </>

                    )}
                </Flex>
            </Flex>

            {/* Mobile Bottom Navigation */}
            <Flex
                direction="row"
                justify="end"
                align="end"
                className="md:hidden fixed bottom-0 left-0 right-0 z-50"
                style={{
                    position: 'fixed',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    width: '100%',
                    // Add these properties to prevent movement
                    minHeight: '80px',
                    maxHeight: '80px',
                    WebkitTransform: 'translateZ(0)', // Forces GPU acceleration
                    transform: 'translateZ(0)',
                    willChange: 'transform' // Hints the browser about the animation
                }}
            >
                <Flex direction="row" justify="center" align="center" gap="0"
                    style={{
                        boxShadow: '0 2px 5px var(--gray-11)',
                        backgroundColor: 'var(--gray-12)',
                    }}
                >
                    {sidenavItems[role].map(({ path, icon: Icon, label }) => (
                        <Link
                            key={path}
                            to={path}
                            style={{
                                padding:'20px',
                                color: 'var(--gray-10)',
                                // color: isPathActive(path) ? 'var(--accent-11)' : 'var(--gray-10)',
                                ...(isPathActive(path) && {
                                    backgroundColor: 'var(--accent-12)',
                                    color: 'var(--gray-3)',
                                })
                            }}
                            className="flex flex-col items-center justify-center p-2"
                        >
                            <Icon className="size-6" />
                            {/* <Text size="1" className="mt-1">
                                {label}
                            </Text> */}
                        </Link>
                    ))}
                </Flex>
            </Flex>

            {/* Mobile Upgrade Button - Adjust position to account for fixed nav */}
            {role === 'student' &&
                account?.paymentDetails?.status !== 'active' && (
                    <>
                        {loading ? (
                            <div className='flex justify-center items-center mt-4 md:hidden'>
                                <Spinner size={16} />
                            </div>

                        ) : (
                            <div className='flex flex-col gap-2 md:hidden fixed z-50'>
                                <div
                                    className="md:hidden fixed z-50"
                                    style={{
                                        bottom: '80px', // Increased to ensure it's above the nav
                                        left: '1rem',
                                        right: '1rem'
                                    }}
                                >
                                    <Link to="/student/payments">
                                        <Button className="w-full bg-green-600 text-white rounded-lg py-3">
                                            Upgrade Plan
                                        </Button>
                                    </Link>
                                </div>

                                {/* {!account?.details?.trial_started_at && (
                                    <Button onClick={handleTrial} className="w-full bg-red-600 text-white rounded-lg py-3 cursor-pointer">
                                        Claim 2-Week Trial
                                    </Button>
                                )} */}

                            </div>

                        )}
                    </>
                )}
        </>
    );
}
